import { TokenListMetadata, OracleType } from "@bridgesplit/abf-sdk";
import { MarketAssetView } from "@bridgesplit/abf-react";

import { getOracleMeta } from "../../common/oracle";

// Extended collateral entry that includes oracle information for differentiation
export type ExtendedCollateralEntry = {
    metadata: TokenListMetadata;
    // Unique key: mint or mint|oracleAddress for multi-oracle collateral
    key: string;
    // Oracle info for display (only for multi-oracle collateral)
    oracleInfo?: {
        type: OracleType;
        address: string;
        name: string;
    };
    // Original mint (for grouping duplicates)
    mint: string;
    // LTV info for display
    ltv: number;
    liquidationThreshold: number;
    // Market information address
    marketInformationAddress: string;
};

/**
 * Helper function to check if a collateral has multiple oracle options
 * with different oracle addresses
 */
export function hasMultipleOracles(
    mint: string,
    collateralToMarketInfoOptions: Map<string, MarketAssetView[]> | undefined
): boolean {
    const options = collateralToMarketInfoOptions?.get(mint);
    if (!options || options.length <= 1) return false;

    const uniqueOracleAddresses = new Set(options.map((opt) => opt.asset.oracleAccount));

    return uniqueOracleAddresses.size > 1;
}

/**
 * Creates extended collateral entries, expanding collateral with multiple oracle addresses
 * into separate selectable options.
 * For collaterals already in the strategy, only show the strategy's existing oracle.
 */
export function createExtendedCollateralEntries(
    tokens: TokenListMetadata[] | undefined,
    collateralToMarketInfoOptions: Map<string, MarketAssetView[]> | undefined,
    existingStrategyCollaterals?: Map<string, { oracleAddress: string; oracleType: number }>,
    enableMultiOracleSelection?: boolean
): ExtendedCollateralEntry[] {
    if (!tokens) return [];

    const extendedEntries: ExtendedCollateralEntry[] = [];

    for (const metadata of tokens) {
        const mint = metadata.mint;

        // Check if this collateral already exists in the strategy
        const strategyCollateral = existingStrategyCollaterals?.get(mint);

        if (strategyCollateral) {
            // Collateral already in strategy: only create ONE entry with the strategy's oracle
            const options = collateralToMarketInfoOptions?.get(mint) || [];
            const matchingOption = options.find((opt) => opt.asset.oracleAccount === strategyCollateral.oracleAddress);

            if (matchingOption) {
                // Use simple key (no oracle address suffix) since there's only one option
                extendedEntries.push({
                    metadata,
                    key: mint,
                    mint,
                    ltv: matchingOption.asset.ltv,
                    liquidationThreshold: matchingOption.asset.liquidationThreshold,
                    marketInformationAddress: matchingOption.market.address
                });
            } else {
                // Fallback if we can't find matching option (shouldn't happen)
                const defaultOption = options[0];
                extendedEntries.push({
                    metadata,
                    key: mint,
                    mint,
                    ltv: defaultOption?.asset.ltv || 0,
                    liquidationThreshold: defaultOption?.asset.liquidationThreshold || 0,
                    marketInformationAddress: defaultOption?.market.address || ""
                });
            }
            continue;
        }

        // Collateral NOT in strategy: show multiple oracle options only if enabled
        const hasMultipleOracleOptions =
            enableMultiOracleSelection && hasMultipleOracles(mint, collateralToMarketInfoOptions);

        if (hasMultipleOracleOptions) {
            // Create separate entries for each oracle option
            const options = collateralToMarketInfoOptions?.get(mint);
            if (!options) continue;

            // Deduplicate by oracle address, keeping highest LTV for each address
            const oracleAddressMap = new Map<string, MarketAssetView>();
            for (const opt of options) {
                const oracleAddress = opt.asset.oracleAccount;
                const existing = oracleAddressMap.get(oracleAddress);
                if (!existing || opt.asset.ltv > existing.asset.ltv) {
                    oracleAddressMap.set(oracleAddress, opt);
                }
            }

            // Create entry for each unique oracle address
            for (const opt of oracleAddressMap.values()) {
                const oracleMeta = getOracleMeta(opt.asset.oracleProvider);
                extendedEntries.push({
                    metadata,
                    key: `${mint}|${opt.asset.oracleAccount}`,
                    oracleInfo: {
                        type: opt.asset.oracleType,
                        address: opt.asset.oracleAccount,
                        name: oracleMeta?.name || "Unknown"
                    },
                    mint,
                    ltv: opt.asset.ltv,
                    liquidationThreshold: opt.asset.liquidationThreshold,
                    marketInformationAddress: opt.market.address
                });
            }
        } else {
            // Single oracle option - use simple key
            const options = collateralToMarketInfoOptions?.get(mint);
            const defaultOption = options?.[0];

            extendedEntries.push({
                metadata,
                key: mint,
                mint,
                ltv: defaultOption?.asset.ltv || 0,
                liquidationThreshold: defaultOption?.asset.liquidationThreshold || 0,
                marketInformationAddress: defaultOption?.market.address || ""
            });
        }
    }

    return extendedEntries;
}
